import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { addDays, addYears, format, isValid, startOfDay } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { FILL_LATER_LEAD_TIME_DAYS } from 'common/constants/fillLater';
import useRecipientAddresses from 'common/hooks/useRecipientAddresses';
import NewPatientAddressButton from 'modules/PatientView/components/ShippingTab/NewPatientAddressButton';
import PatientBanner from 'common/modules/PatientBanner';
import { splitLineItemQuantity } from 'store/thunks/patientThunks';
import SplitOrderByQuantityItemPanel from './SplitOrderByQuantityItemPanel';
import { SubmitInfo, mutedLabel } from './SplitOrderByQuantityTiles';
import {
  buildItemSummaryLine,
  splitTotalError,
  suggestedDateForNextRow,
  suggestedFirstRowDate,
  toNumber,
  totalToFillLater,
} from './splitOrderByQuantityHelpers';

const emptySplit = (fillDate) => ({ quantity: '', fillDate });

const SplitOrderByQuantityDialog = ({
  open,
  onClose,
  onSuccess,
  mpi,
  orderId,
  lineItems,
  orderAddress,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [addressByItem, setAddressByItem] = useState({});
  const [blockedItems, setBlockedItems] = useState({});
  const [verifiedItems, setVerifiedItems] = useState({});
  const addressBlocked = lineItems.some((lineItem) => blockedItems[lineItem.lineItemId]);
  const {
    addresses,
    loading: addressesLoading,
    reload: reloadAddresses,
  } = useRecipientAddresses({
    orderId,
    enabled: open,
  });
  const storedAddressCount = useSelector(({ patient }) => patient?.[mpi]?.addresses?.length ?? 0);

  useEffect(() => {
    if (open && storedAddressCount) reloadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedAddressCount]);
  // Rows are identified by a stable key, per line item, so removing a middle row does not
  // remount the rest of that item's rows.
  const [rowKeys, setRowKeys] = useState(() => lineItems.map(() => [0]));
  // Splitting several items at once means several panels: the first is open so the dialog
  // never lands fully collapsed, and the rest are opened as the user works down the list.
  const [expandedIds, setExpandedIds] = useState(() =>
    lineItems.length === 1 ? lineItems.map((item) => item.lineItemId) : [lineItems[0]?.lineItemId]
  );

  const isSingleItem = lineItems.length === 1;

  // The lead time SUGGESTS this date, it no longer restricts it (POM-6197). Row 0 opens here
  // and each added row opens a lead time past the one above it, but every one of those dates
  // stays editable down to tomorrow.
  const suggestedDate = useMemo(
    () => addDays(startOfDay(new Date()), FILL_LATER_LEAD_TIME_DAYS),
    []
  );

  // The only date rule left, matching splitLineItemQuantity: strictly after today. An early
  // date cannot jump the queue -- POM-6150 holds each fill at WAITING_SHIPMENT until the items
  // before it have shipped -- so there is nothing for the picker to protect against.
  const earliestSelectableDate = useMemo(() => addDays(startOfDay(new Date()), 1), []);

  const latestSelectableDate = useMemo(() => addYears(startOfDay(new Date()), 1), []);

  // POM-6254: one starting date per item, a lead time past whatever that item already has
  // scheduled. The dialog could always step its own rows; what it could not see was the splits
  // made in an earlier sitting, so reopening an order restarted at today and the second split
  // landed before the first. listPatientOrders now returns them.
  const firstRowDates = useMemo(
    () =>
      lineItems.map((item) =>
        suggestedFirstRowDate({
          lineItem: item,
          suggestedDate,
          leadTimeDays: FILL_LATER_LEAD_TIME_DAYS,
          latestDate: latestSelectableDate,
        })
      ),
    [lineItems, suggestedDate, latestSelectableDate]
  );

  const [initialValues] = useState(() => ({
    items: lineItems.map((item, index) => ({
      lineItemId: item.lineItemId,
      splits: [emptySplit(firstRowDates[index])],
    })),
  }));

  // final-form gives field-level errors precedence over record-level ones, so the message
  // the user reads has to be produced here as well as in validate() below.
  const dateOutOfWindowMessage = () => 'Fill dates must be after today and within one year';

  const validateFillDate = () => (value) => {
    if (!value || !isValid(value)) return undefined;

    if (startOfDay(value) < earliestSelectableDate) return dateOutOfWindowMessage();

    if (startOfDay(value) > latestSelectableDate) return dateOutOfWindowMessage();

    return undefined;
  };

  // Each item is validated on its own quantities: one item being over does not make
  // another item's rows invalid, it only keeps the whole submission from going.
  const validate = ({ items }) => {
    const errors = { items: [] };

    (items ?? []).forEach((item, itemIndex) => {
      const splits = item?.splits ?? [];
      const itemErrors = { splits: [] };

      splits.forEach((split, index) => {
        const dateError = validateFillDate()(split?.fillDate);

        itemErrors.splits[index] = dateError ? { fillDate: dateError } : {};
      });

      // Not tied to a field, so no row shows it — it still keeps the form invalid.
      const totalError = splitTotalError({
        splits,
        dispenseQuantity: toNumber(lineItems[itemIndex]?.quantity),
      });

      if (totalError) itemErrors.splitTotal = totalError;

      errors.items[itemIndex] = itemErrors;
    });

    return errors;
  };

  const handleClose = () => {
    setRowKeys(lineItems.map(() => [0]));
    setIsLoading(false);
    onClose();
  };

  const handleTogglePanel = (lineItemId) => () =>
    setExpandedIds((ids) =>
      ids.includes(lineItemId) ? ids.filter((id) => id !== lineItemId) : [...ids, lineItemId]
    );

  const handleAddSplit = (form, splits, itemIndex) => {
    // Only a suggestion: the new row opens a lead time past the one above it, and the user
    // can move it to any day after today.
    // Held at the end of the window rather than stepped past it: a row that opened on an
    // invalid date would show its own error before the user had touched it.
    const nextDate = suggestedDateForNextRow({
      splits,
      // Falls back to this item's own starting date rather than the dialog's, so removing every
      // row and adding one back lands where the item began instead of on today plus a lead time.
      suggestedDate: firstRowDates[itemIndex],
      leadTimeDays: FILL_LATER_LEAD_TIME_DAYS,
      latestDate: latestSelectableDate,
    });

    form.change(`items[${itemIndex}].splits[${splits.length}]`, emptySplit(nextDate));
    setRowKeys((keys) =>
      keys.map((itemKeys, index) =>
        index === itemIndex ? [...itemKeys, Math.max(...itemKeys) + 1] : itemKeys
      )
    );
  };

  const handleRemoveSplit = (form, splits, itemIndex, rowIndex) => {
    form.change(
      `items[${itemIndex}].splits`,
      splits.filter((_, index) => index !== rowIndex)
    );
    setRowKeys((keys) =>
      keys.map((itemKeys, index) =>
        index === itemIndex ? itemKeys.filter((_, keyIndex) => keyIndex !== rowIndex) : itemKeys
      )
    );
  };

  // One call for the whole dialog. The API already takes a list, and the backend applies it
  // in a single transaction, so a multi-item submission is all-or-nothing rather than a
  // per-item loop that can half-succeed.
  const handleSubmit = ({ items }) => {
    setIsLoading(true);

    const payload = items.map((item, index) => ({
      orderId,
      lineItemId: lineItems[index].lineItemId,
      splits: item.splits.map((split) => ({
        splitQuantity: String(toNumber(split.quantity)),
        scheduledFor: format(startOfDay(split.fillDate), 'yyyy-MM-dd'),
        ...(addressByItem[lineItems[index].lineItemId]
          ? { addressDirectoryId: addressByItem[lineItems[index].lineItemId] }
          : {}),
      })),
      ...(verifiedItems[lineItems[index].lineItemId] ? { isManuallyVerified: true } : {}),
    }));

    const scheduleCount = payload.reduce((total, item) => total + item.splits.length, 0);
    // One message for the whole submission, naming the Rx while a single item can still be
    // named and falling back to the item count once several were split together.
    const successMessage = () => {
      if (!isSingleItem) {
        return `${scheduleCount} fill later schedules created for ${lineItems.length} line items`;
      }

      return scheduleCount > 1
        ? `${scheduleCount} fill later schedules created for ${lineItems[0].rxNumber}`
        : `Fill later scheduled for ${lineItems[0].rxNumber}`;
    };

    return dispatch(splitLineItemQuantity({ mpi, items: payload }))
      .then((result) => {
        if (!result) return;

        enqueueSnackbar(successMessage(), { variant: 'success' });
        onSuccess?.();
        handleClose();
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-describedby='SplitOrderByQuantity-Dialog-header'
      fullWidth
      // Wider than the single-item dialog it grew out of: a panel header carries the drug
      // name, the Rx number and the running total on one line, and several panels stack.
      PaperProps={{ sx: { maxWidth: '780px' } }}
    >
      <DialogTitle id='SplitOrderByQuantity-header' sx={{ pb: 1 }}>
        Split by quantity and Fill Later
      </DialogTitle>

      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={validate}
        render={({ handleSubmit: submit, invalid, values, form }) => {
          const items = values.items ?? [];
          const summaryLines = items
            .map((item, index) => {
              const splits = item?.splits ?? [];
              const dispenseQuantity = toNumber(lineItems[index]?.quantity);

              return buildItemSummaryLine({
                lineItem: lineItems[index],
                splits,
                fillsNow: dispenseQuantity - totalToFillLater(splits),
                dispenseQuantity,
              });
            })
            .filter(Boolean);

          return (
            <form noValidate onSubmit={submit}>
              <DialogContent sx={{ pt: 0 }}>
                <Grid container sx={{ mb: 2 }}>
                  <PatientBanner mpi={mpi} />
                </Grid>

                <Box display='flex' alignItems='center' sx={{ mb: 1 }}>
                  <Typography variant='h6'>
                    {isSingleItem
                      ? 'Quantity to fill later'
                      : `${lineItems.length} line items selected`}
                  </Typography>
                  <Tooltip
                    title='A new order is created for each split quantity on the fill date you pick. The remaining quantity stays on this order.'
                    placement='top'
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 15, color: 'text.secondary' }} />
                  </Tooltip>
                  {/* Stated once for the whole dialog: every panel below belongs to this
                      order, so repeating it in each of them said nothing. */}
                  <Typography sx={{ ml: 'auto', color: mutedLabel, fontSize: '0.85em' }}>
                    {`Order : ${orderId}`}
                  </Typography>
                  <Box
                    sx={{
                      ml: 1.5,
                      flexShrink: 0,
                      '& .MuiButton-root': {
                        py: 0.3,
                        px: 1.25,
                        minWidth: 0,
                        fontSize: '0.72rem',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <NewPatientAddressButton mpi={mpi} showDeliveryInstructions={false}>
                      Add a new address
                    </NewPatientAddressButton>
                  </Box>
                </Box>

                <Box
                  sx={{
                    // Each item is a panel carrying an address field and however many split rows
                    // the operator adds, so this grows while they work - not just with the order.
                    // Left unbounded it pushes the footer off the screen and Split N Items is only
                    // reachable after scrolling the whole dialog.
                    maxHeight: 400,
                    overflowY: 'auto',
                  }}
                >
                  {items.map((item, index) => {
                    const splits = item?.splits ?? [];
                    const lineItem = lineItems[index];

                    return (
                      <SplitOrderByQuantityItemPanel
                        key={lineItem.lineItemId}
                        lineItem={lineItem}
                        index={index}
                        splits={splits}
                        rowKeys={rowKeys[index]}
                        expanded={expandedIds.includes(lineItem.lineItemId)}
                        onToggle={handleTogglePanel(lineItem.lineItemId)}
                        onAddSplit={() => handleAddSplit(form, splits, index)}
                        onRemoveSplit={(rowIndex) =>
                          handleRemoveSplit(form, splits, index, rowIndex)
                        }
                        totalError={splitTotalError({
                          splits,
                          dispenseQuantity: toNumber(lineItem?.quantity),
                        })}
                        earliestSelectableDate={earliestSelectableDate}
                        latestSelectableDate={latestSelectableDate}
                        validateFillDate={validateFillDate}
                        isSingleItem={isSingleItem}
                        addresses={addresses}
                        orderAddress={orderAddress}
                        addressesLoading={addressesLoading}
                        addressDirectoryId={addressByItem[lineItem.lineItemId] ?? null}
                        onAddressAcknowledgedChange={(isAcknowledged) =>
                          setVerifiedItems((current) => ({
                            ...current,
                            [lineItem.lineItemId]: isAcknowledged,
                          }))
                        }
                        onAddressChange={(value) =>
                          setAddressByItem((current) => ({
                            ...current,
                            [lineItem.lineItemId]: value,
                          }))
                        }
                        onAddressBlockedChange={(isBlocked) =>
                          setBlockedItems((current) => ({
                            ...current,
                            [lineItem.lineItemId]: isBlocked,
                          }))
                        }
                      />
                    );
                  })}
                </Box>

                <SubmitInfo>
                  {summaryLines.length ? (
                    summaryLines.map((line) => (
                      <Box key={line} component='span' sx={{ display: 'block' }}>
                        {line}
                      </Box>
                    ))
                  ) : (
                    <>
                      Enter a quantity and fill date for each item you want to schedule. Each
                      scheduled quantity becomes its own new order on its fill date. Whatever you
                      leave unscheduled stays on this order and fills now.
                    </>
                  )}
                </SubmitInfo>
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleClose}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={isLoading}
                  variant='contained'
                  type='submit'
                  disabled={invalid || !summaryLines.length || addressBlocked}
                >
                  {isSingleItem ? 'Split Order' : `Split ${lineItems.length} Items`}
                </LoadingButton>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};

SplitOrderByQuantityDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  orderAddress: PropTypes.shape({}),
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string,
      itemName: PropTypes.string,
      itemType: PropTypes.string,
      strength: PropTypes.string,
      form: PropTypes.string,
      ndc: PropTypes.string,
      rxNumber: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      prescribedQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
};

SplitOrderByQuantityDialog.defaultProps = {
  onSuccess: null,
  orderAddress: null,
};

export default SplitOrderByQuantityDialog;
